import log from '@/services/logger/log';
import notificationPermissionService from '@/services/pushNotification/notificationPermissionService';
import notificationUIService from '@/services/pushNotification/notificationUIService';
import PUSH_NOTIFICATION_CONFIG from '@/services/pushNotification/pushNotificationConfig';
import subscriptionService from '@/services/pushNotification/subscriptionService';
import {selectConsumerId} from '@/state/selectors/consumer';
import {getState} from '@/state/store';

import serviceWorkerService from '../serviceWorkerService';
import notificationStorageService from '../storage/notificationStorageService';

const subscribe = async () => {
    try {
        const subscription = await subscriptionService.createWebPushSubscription();

        if (subscription) {
            log.debug('Push subscription data received successfully');
            await subscriptionService.storeSubscriptionToBackend(subscription);
        }

        return subscription;
    } catch (e) {
        log.info(`Subscription error: ${e}`);
        throw e;
    }
};

const requestSubscription = async () => {
    try {
        notificationStorageService.setIsPushNotificationDisabled(false);
        await subscribe();
    } catch (e) {
        throw e;
    }
};

const checkIfPushNotificationSupported = async () => {
    if (!('PushManager' in window)) return false;
    if (!('serviceWorker' in navigator)) return false;
    if (!('Notification' in window)) return false;

    const swReg = await serviceWorkerService.registerServiceWorker();

    return !!swReg;
};

const checkIfNotificationScreenShouldBeDisplayed = async () => {
    const isPushNotificationSupported = await checkIfPushNotificationSupported();

    // Push notifications are not supported in the browser
    if (!isPushNotificationSupported) return false;

    // Push notifications are disabled by user
    if (notificationStorageService.getIsPushNotificationDisabled()) return false;

    // User denied Push Notifications before
    // TODO: add consumerId check, so askPermission for new consumer
    if (await notificationPermissionService.checkIfNotificationIsDenied()) return false;

    // Push subscription already created
    if (subscriptionService.getStoredSubscriptionInfo()) return false;

    // Permission is granted, get subscription in background(usePushNotificationSubscriptionManager)
    if (await notificationPermissionService.checkIfNotificationIsGranted()) {
        return false;
    }

    return true;
};

const checkSubscriptionInfoForNewUser = async () => {
    const {consumerId: subscriptionConsumerId} = subscriptionService.getStoredSubscriptionInfo() || {};

    if (!subscriptionConsumerId) return;

    const consumerId = selectConsumerId(getState());

    if (subscriptionConsumerId === consumerId) return;

    notificationUIService.clearAppNotifications();
    await subscriptionService.unsubscribe();
    subscriptionService.clearSubscriptionInfo();
};

const disablePushNotifications = async () => {
    notificationStorageService.setIsPushNotificationDisabled(true);
    await subscriptionService.unsubscribeAndDeleteSubscription(true);
};

const initializeFirebaseInServiceWorker = async () => {
    try {
        const {FIREBASE_CONFIG} = PUSH_NOTIFICATION_CONFIG;

        if (!FIREBASE_CONFIG) {
            log.info('Firebase config is not available');
            return;
        }

        await serviceWorkerService.sendFirebaseConfig(FIREBASE_CONFIG);
        log.debug('Firebase config initialized in service worker');
    } catch (e) {
        log.debug(`Error initializing Firebase in service worker: ${e}`);
    }
};

export default {
    requestSubscription,
    subscribe,
    checkIfPushNotificationSupported,
    checkIfNotificationScreenShouldBeDisplayed,
    checkSubscriptionInfoForNewUser,
    disablePushNotifications,
    initializeFirebaseInServiceWorker,
};
